/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module UpdateModule */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    Type,
    ViewChild,
} from '@angular/core';

import {
    IUpgradeStatusInfo,
    SeGroupErrorRecovery,
    UpgradeFsmUiState,
    UpgradeOperation,
} from 'generated-types';

import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { AviModalService } from 'ajs/modules/core/services/avi-modal/avi-modal.service';
import { DialogService } from 'ng/modules/core/services/dialog.service';
import { ITSEGroup } from 'ajs/modules/service-engine-group/factories/se-group.item.factory';
import { L10nService } from '@vmw/ngx-vip';
import { SEGroupCollection } from 'ajs/modules/service-engine-group/factories/se-group.collection.factory';
import { StringService } from 'string-service';
import { Subject, takeUntil } from 'rxjs';
import { SystemReportsCollection } from 'ng/modules/collections/factories/system-reports.collection.factory';
import { UpdateService } from 'ng/modules/update/services/update.service';
import * as globalL10n from 'global-l10n';

import {
    IAviCollectionDataGridConfig,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import {
    ImageCollection,
    TImageCollection,
} from 'ajs/modules/upgrade/factories/image-collection.factory';

import { UpdateStore } from '../update.store';
import { UpgradeTranscriptModalComponent } from '../upgrade-transcript-modal/upgrade-transcript-modal.component';

import {
    IPrecheckProgressConfig,
    IUpdateConfig,
    IUserSelectedOptions,
    nodeTypesHash,
    UpdateWizardStage,
    UpgradePreChecksStatusTypeHash,
    UpgradeStateGroupTypesHash,
    UpgradeType,
} from '../../update.types';

import './seg-update-page.component.less';
import * as l10n from './seg-update-page.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TSeGroupCollection = typeof SEGroupCollection;

/**
 * Type of node object we'll be pushing in nodes array while filtering for nodes with latest
 * operation timestamp.
 */
interface ISegNode {
    uuid: string;
    timeStamp: number;
    node: IUpgradeStatusInfo;
}

const SEG_UPGRADE_TRANSCRIPT_DIALOG_ID = 'SEG_UPGRADE_TRANSCRIPT_DIALOG_ID';

/**
 * Set of UpgradePreCheck States to determine if the latest operation is Pre-checks one or
 * show the transcript option in case of pre-checks operation.
 */
const PRE_CHECK_STATUS = new Set([
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_ERROR,
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_IN_PROGRESS,
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_STARTED,
    UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_WARNING,
]);

/**
 * Set of Upgrade States.
 */
const UPGRADE_STATES_SET = new Set([
    UpgradeStateGroupTypesHash.UPGRADE_STATE_GROUP_IN_PROGRESS,
    UpgradeStateGroupTypesHash.UPGRADE_STATE_GROUP_COMPLETED,
    UpgradeStateGroupTypesHash.UPGRADE_STATE_GROUP_ERROR,
]);

/**
 * Constant for string value received from backend for
 * operation type in System Report.
 */
const PRE_CHECKS = 'PRE-CHECKS';

/**
 * @description Component for SEG Update page.
 * @author Sarthak Kapoor, Dhanashree Tilak, Deepak Chaudhary
 */
@Component({
    selector: 'seg-update-page',
    templateUrl: './seg-update-page.component.html',
})
export class SegUpdatePageComponent implements OnInit, OnDestroy {
    @ViewChild('statusTemplateRef', { static: true })
    public readonly statusTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('segUpdateExpanderTemplateRef', { static: true })
    public readonly segUpdateExpanderTemplateRef: TemplateRef<HTMLElement>;

    public readonly nodeType = nodeTypesHash.NODE_SE_GROUP;

    public readonly seGroupCollection: SEGroupCollection;

    public seGroupGridConfig: IAviCollectionDataGridConfig;

    public readonly upgradeStateGroupTypesHash = UpgradeStateGroupTypesHash;

    /**
     * List of SEG IDs selected for upgrade/pre-checks.
     */
    public selectedSegIds: string[] = [];

    /**
     * List of nodes received from backend on trigger of pre-checks.
     */
    public preCheckNodes = this.updateStore.preCheckNodes$;

    /**
     * Flag to keep a track if pre checks are triggered.
     */
    public arePreChecksTriggered = false;

    /**
     * Flag to identify if right rail cards can be shown.
     */
    public showRightRailCards = false;

    /**
     * Wizard state
     */
    public isWizardOpen: boolean;

    /**
     * The current stage of the Upgrade Wizard.
     */
    public currentUpdateWizardStage: UpdateWizardStage | undefined;

    /**
     * The progress wizard configuration.
     */
    public progressWizardInfo: IPrecheckProgressConfig;

    /**
     * Upgrade Config details
     */
    public upgradeConfig: IUpdateConfig = {};

    /**
     * Overall state of pre-checks for all the selected nodes.
     */
    public readonly overallPreChecksState$ = this.updateStore.overallPreChecksState$;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    public systemReportId: string;

    /**
     * Flag to keep a track if user is currently on the same page from where
     * the update was triggered.
     */
    private isCurrentPageUpdate = false;

    /**
     * Flag to keep a track if the currently in progress pre checks complete.
     */
    private arePreChecksComplete = false;

    /**
     * Hold the latest value of configuration user selected while triggering the update.
     */
    private userSelectedOptions: IUserSelectedOptions = {};

    /**
     * Subject used to unsubscribe observables.
     */
    private ngDestroyed$ = new Subject();

    /**
     * Store ids of selected SEG node for Update operations.
     */
    public segUpgradeNodeList: ITSEGroup[] = [];

    constructor(
        private readonly l10nService: L10nService,
        private readonly updateService: UpdateService,
        @Inject(SEGroupCollection)
        private readonly SEGroupCollection: TSeGroupCollection,
        private readonly authService: Auth,
        private readonly updateStore: UpdateStore,
        private readonly dialogService: DialogService,
        private readonly aviModal: AviModalService,
        @Inject(SystemReportsCollection)
        private readonly SystemReportCollectionClass: typeof SystemReportsCollection,
        private readonly stringService: StringService,
        @Inject(ImageCollection)
        private readonly ImageCollectionFactory: TImageCollection,
    ) {
        this.seGroupCollection = new this.SEGroupCollection({
            dataFields: [
                'config',
                'upgradestatus',
            ],
        });

        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Verify if the selected SEGs are valid for upgrade.
     */
    private static isSegSelectionValid(segList: ITSEGroup[]): boolean {
        return UpdateService.isSegListUpgradable(segList);
    }

    /** @override */
    public ngOnInit(): void {
        this.setSegGridConfig();
        this.fetchStatusUpgradeInfo();

        this.updateStore.preChecksTriggered$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((preChecksTriggered: boolean) => {
                this.arePreChecksTriggered = preChecksTriggered;

                if (this.arePreChecksTriggered) {
                    this.seGroupCollection.load();
                }
            });

        this.updateStore.isUpgradeTriggeredFromSystemUpdatePage$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((isSystemUpdate: boolean) => this.isCurrentPageUpdate = !isSystemUpdate);

        this.updateStore.preChecksCompleted$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((preChecksCompleted: boolean) => this.arePreChecksComplete = preChecksCompleted);

        this.updateStore.userSelectedOptions$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((options: IUserSelectedOptions) => this.userSelectedOptions = options);
    }

    /**
     * @override
     * Reset isPreCheckFlow state in store, once user navigates away from the SEG page.
     */
    public ngOnDestroy(): void {
        const { updateStore } = this;

        updateStore.setPreCheckNodesState([]);
        updateStore.resetPreChecksTriggeredState(false);
        updateStore.resetPreChecksCompletedState(false);
        updateStore.resetOverallPreChecksState();
        updateStore.setIsPreCheckFlowState(false);
        updateStore.resetIsPreCheckOnlyFlowState();
        updateStore.setIsUpdateTriggeredWithWarningState(false);
        this.seGroupCollection.destroy();

        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Check if status of node is not among success, error or loading.
     */
    // eslint-disable-next-line class-methods-use-this
    public isNodeStatusKnown(row: ITSEGroup): boolean {
        return UPGRADE_STATES_SET.has(row.stateGroup as unknown as UpgradeFsmUiState) ||
            PRE_CHECK_STATUS.has(row.upgradeReadiness?.state?.state);
    }

    /**
     * Update state when the wizard is closed.
     */
    public onClose(): void {
        this.isWizardOpen = false;
        this.currentUpdateWizardStage = undefined;
    }

    /**
     * Open Pre-check wizard to view Pre-check progress.
     */
    public openProgressWizard(config: IPrecheckProgressConfig): void {
        this.upgradeConfig = {
            upgradeType: UpgradeType.UPGRADE_TYPE_SEG_UPGRADE,
        };
        this.progressWizardInfo = config;
        this.currentUpdateWizardStage = UpdateWizardStage.PRE_CHECK;
        this.isWizardOpen = true;
    }

    private setSegGridConfig(): void {
        const {
            authService,
            l10nService,
            seGroupCollection: { objectName },
        } = this;

        const fields = [
            {
                id: 'name',
                label: l10nService.getMessage(globalL10nKeys.nameLabel),
                transform: (row: ITSEGroup) => row.getName(),
            },
            {
                id: 'version',
                label: l10nService.getMessage(globalL10nKeys.versionLabel),
                transform: (row: ITSEGroup) => row.getVersionWithoutPatch(true),
            },
            {
                id: 'patch',
                label: l10nService.getMessage(l10nKeys.patchLabel),
                transform: (row: ITSEGroup) => row.patchVersion || '-',
            },
            {
                id: 'virtual-services',
                label: l10nService.getMessage(globalL10nKeys.virtualServicesLabel),
                transform: (row: ITSEGroup) => row.numOfVs.toString(),
            },
            {
                id: 'service-engines',
                label: l10nService.getMessage(globalL10nKeys.serviceEnginesLabel),
                transform: (row: ITSEGroup) => row.numOfSe.toString(),
            },
            {
                id: 'cloud',
                label: l10nService.getMessage(globalL10nKeys.cloudLabel),
                transform: (row: ITSEGroup) => row.cloudName,
            },
            {
                id: 'status',
                label: l10nService.getMessage(globalL10nKeys.statusLabel),
                templateRef: this.statusTemplateRef,
            },
        ];

        if (authService.allTenantsMode()) {
            fields.push({
                id: 'tenant',
                label: l10nService.getMessage(globalL10nKeys.tenantLabel),
                transform: (row: ITSEGroup) => row.getTenantId(),
            });
        }

        this.seGroupGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.seGroupCollection,
            defaultSorting: 'name',
            fields,
            expandedContentTemplateRef: this.segUpdateExpanderTemplateRef,
            singleactions: [{
                id: 'view-transcipt',
                label: l10nService.getMessage(l10nKeys.viewTranscriptLabel),
                shape: 'chat-bubble',
                onClick: (row: ITSEGroup) => this.openTranscriptModal(row),
                hidden: (row: ITSEGroup) => !this.isNodeStatusKnown(row),
            }],
            multipleactions: [
                {
                    id: 'update',
                    label: l10nService.getMessage(l10nKeys.updateLabel),
                    onClick: (rows: ITSEGroup[]) => this.openCompatibleUpdatesPopup(rows),
                    disabled: (rows: ITSEGroup[]) => {
                        return !SegUpdatePageComponent.isSegSelectionValid(rows);
                    },
                },
            ],
            layout: {
                disableCreate: true,
                hideCreate: true,
                hideDelete: true,
                hideEdit: true,
                placeholderMessage: l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
            rowSelectionDisabled: (row: ITSEGroup) => row.isUpgradeInProgress() ||
                row.arePrechecksForNodeInProgress(),
        };
    }

    /**
     * Fetch the overall state of the latest performed operation.
     */
    private async fetchStatusUpgradeInfo(): Promise<void> {
        const {
            nodeType,
            updateService,
        } = this;

        const response: { data: { count: number, results: IUpgradeStatusInfo[] } } =
            await updateService.getUpgradeStatus(nodeType);

        if (response?.data?.count && response.data.results?.length) {
            this.filterLatestOperationTimeStamp(response.data.results);
        }
    }

    /**
     * Find the time stamp of the latest operation performed on any node.
     */
    private filterLatestOperationTimeStamp(upgradeStatusNodes: IUpgradeStatusInfo[]): void {
        const nodes: ISegNode[] = [];
        let nodeWithoutPreCheckStateCounter = 0;
        let latestOperationStartTimeStamp = 0;

        for (const node of upgradeStatusNodes) {
            const state = node.upgrade_readiness?.state?.state;

            if (state) {
                const { start_time: upgradeStartTime } = node;
                const { start_time: preChecksStartTime } = node.upgrade_readiness;

                if (!upgradeStartTime && !preChecksStartTime) {
                    // Case where no upgrade/pre-check operation has been triggered for the node.
                    nodeWithoutPreCheckStateCounter += 1;
                } else if (upgradeStartTime && !preChecksStartTime) {
                    /**
                     * Case where no fresh pre-check operation has been triggered but
                     * a previous upgrade operation exists for the node.
                     */
                    const upgradeStartTimeStamp = new Date(upgradeStartTime).getTime();

                    if (latestOperationStartTimeStamp) {
                        if (upgradeStartTimeStamp > latestOperationStartTimeStamp) {
                            latestOperationStartTimeStamp = upgradeStartTimeStamp;
                        }
                    } else {
                        latestOperationStartTimeStamp = upgradeStartTimeStamp;
                    }

                    nodes.push({
                        uuid: node.uuid,
                        timeStamp: upgradeStartTimeStamp,
                        node,
                    });
                } else if (!upgradeStartTime && preChecksStartTime) {
                    /**
                     * Case where pre checks have been recently triggered but
                     * no upgrade operation has been performed yet for the node.
                     */
                    const preCheckStartTimeStamp = new Date(preChecksStartTime).getTime();

                    if (latestOperationStartTimeStamp) {
                        if (preCheckStartTimeStamp > latestOperationStartTimeStamp) {
                            latestOperationStartTimeStamp = preCheckStartTimeStamp;
                        }
                    } else {
                        latestOperationStartTimeStamp = preCheckStartTimeStamp;
                    }

                    nodes.push({
                        uuid: node.uuid,
                        timeStamp: preCheckStartTimeStamp,
                        node,
                    });
                } else {
                    /**
                     * Node has both the preChecksStartTime and upgradeStartTime indicating
                     * there has been an upgrade operation for this node and a pre-check operation
                     * has been triggered as well.
                     */
                    const upgradeStartTimeStamp = new Date(upgradeStartTime).getTime();
                    const preCheckStartTimeStamp = new Date(preChecksStartTime).getTime();

                    if (upgradeStartTimeStamp > preCheckStartTimeStamp) {
                        if (latestOperationStartTimeStamp) {
                            if (upgradeStartTimeStamp > latestOperationStartTimeStamp) {
                                latestOperationStartTimeStamp = upgradeStartTimeStamp;
                            }
                        } else {
                            latestOperationStartTimeStamp = upgradeStartTimeStamp;
                        }

                        nodes.push({
                            uuid: node.uuid,
                            timeStamp: upgradeStartTimeStamp,
                            node,
                        });
                    } else {
                        if (latestOperationStartTimeStamp) {
                            if (preCheckStartTimeStamp > latestOperationStartTimeStamp) {
                                latestOperationStartTimeStamp = preCheckStartTimeStamp;
                            }
                        } else {
                            latestOperationStartTimeStamp = preCheckStartTimeStamp;
                        }

                        nodes.push({
                            uuid: node.uuid,
                            timeStamp: preCheckStartTimeStamp,
                            node,
                        });
                    }
                }
            } else {
                /**
                 * If the state doesn't exist, it means there's no info about pre checks available
                 * for this particular node. It'll be treated as update flow node.
                 */
                nodeWithoutPreCheckStateCounter += 1;

                const { start_time: upgradeStartTime } = node;

                if (upgradeStartTime) {
                    const upgradeStartTimeStamp = new Date(upgradeStartTime).getTime();

                    nodes.push({
                        uuid: node.uuid,
                        timeStamp: upgradeStartTimeStamp,
                        node,
                    });
                }
            }
        }

        this.filterNodesWithLatestTimeStamp(
            upgradeStatusNodes,
            nodeWithoutPreCheckStateCounter,
            nodes,
            latestOperationStartTimeStamp,
        );
    }

    /**
     * Set the latest flow type (Upgrade/pre-check) by filtering out the nodes which have the
     * operation start time same as the latest time stamp.
     * Also set the list of such filtered SEG IDs.
     */
    private async filterNodesWithLatestTimeStamp(
        upgradeStatusNodes: IUpgradeStatusInfo[],
        nodeWithoutPreCheckStateCounter: number,
        nodes: ISegNode[],
        latestOperationStartTimeStamp: number,
    ): Promise<void> {
        const { updateStore } = this;

        /**
         * If no pre-checks state exists for any of the nodes, it indicates that the latest operation
         * type was not the pre-checks operation.
         */
        if (nodeWithoutPreCheckStateCounter === upgradeStatusNodes.length) {
            updateStore.setIsPreCheckFlowState(false);
            updateStore.setIsPreCheckOnlyFlowState(false);

            /**
             * If there are any entries in the nodes array, it means the latest operation on these
             * nodes is the upgrade operation.
             */
            if (nodes?.length) {
                this.selectedSegIds = nodes.map(
                    node => node.uuid,
                );
            }

            this.showRightRailCards = true;

            return;
        }

        const nodesWithLatestTimeStamp = nodes.filter(
            node => node.timeStamp === latestOperationStartTimeStamp,
        );

        this.selectedSegIds = nodesWithLatestTimeStamp.map(node => node.uuid);

        const state = nodesWithLatestTimeStamp[0].node.upgrade_readiness?.state?.state;

        if (PRE_CHECK_STATUS.has(state)) {
            if (state === UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_IN_PROGRESS) {
                updateStore.onPreChecksTrigger();
            }

            updateStore.setIsPreCheckFlowState(true);

            const systemReports = nodesWithLatestTimeStamp[0].node.system_report_refs;

            if (systemReports?.length) {
                const isPreCheckOnlyFlow = await this.checkIfPreCheckOnlyFlow(systemReports[0]);

                updateStore.setIsPreCheckOnlyFlowState(isPreCheckOnlyFlow);
            }

            if (state === UpgradePreChecksStatusTypeHash.UPGRADE_PRE_CHECK_WARNING) {
                await this.setSegSuppressWarning(nodes);
            }
        } else {
            updateStore.setIsPreCheckFlowState(false);
        }

        this.showRightRailCards = true;
    }

    /**
     * Set the suppress warning for SEG node after refresh based on response.
     */
    private async setSegSuppressWarning(nodes: ISegNode[]): Promise<void> {
        const {
            node: { upgrade_readiness: upgradeReadiness },
            uuid,
        } = nodes?.[0] ?? {};

        const selectedSegIds = this.seGroupCollection.getItemById(uuid);

        if (!selectedSegIds) {
            return;
        }

        const imageId = upgradeReadiness?.image_ref ?
            this.stringService.slug(upgradeReadiness?.image_ref) :
            this.stringService.slug(upgradeReadiness?.patch_image_ref);

        this.segUpgradeNodeList = [selectedSegIds];
        this.updateStore.setSelectedSegsListState(this.segUpgradeNodeList);

        const imageCollection = new this.ImageCollectionFactory({
            objectName: 'image-raw-inventory',
            params: {
                base_image_version: UpdateService.getSegUpgradeListBaseImageVersion(this.segUpgradeNodeList),
                upgrade_operation: UpgradeOperation.SE_UPGRADE_OP,
            },
        });

        await imageCollection.load();

        const selectedImagesToUpgrade = imageCollection.getItemById(imageId);

        this.upgradeConfig = UpdateService.getSegUpgradeConfig(this.segUpgradeNodeList, [selectedImagesToUpgrade]);
        this.updateStore.setSelectedSegUpgradeImagesState([selectedImagesToUpgrade]);

        const userSelectedOptions = {
            actionOnSegFailure: SeGroupErrorRecovery.SUSPEND_UPGRADE_OPS_ON_ERROR,
            applyToSystem: false,
            isRollback: false,
            upgradeConfig: this.upgradeConfig,
            upgradeType: this.upgradeConfig.upgradeType,
        };

        this.updateStore.setUserSelectedOptionsState(userSelectedOptions);
    }

    /**
     * Open Compatible Updates Pop up.
     */
    private openCompatibleUpdatesPopup(segUpgradeNodeList: ITSEGroup[]): void {
        this.updateStore.setSelectedSegsListState(segUpgradeNodeList);
        this.segUpgradeNodeList = segUpgradeNodeList;
        this.currentUpdateWizardStage = UpdateWizardStage.SEG_COMPATIBLE_UPDATES;
        this.isWizardOpen = true;
    }

    private openTranscriptModal(node: ITSEGroup): void {
        const { upgradeReadiness = {} } = node;

        if (PRE_CHECK_STATUS.has(upgradeReadiness.state?.state)) {
            this.updateStore.openPreCheckTranscript({
                readonly: this.openTranscriptModalInReadOnlyMode(),
                nodeIds: [node.id],
            });
        } else {
            this.openUpgradeTranscriptModal(node);
        }
    }

    /**
     * Open modal to display the progress/state of events of upgrade/rollback operation.
     */
    private openUpgradeTranscriptModal(node: ITSEGroup): void {
        const {
            id,
            nodeType,
        } = node;
        const { dialogService } = this;

        dialogService.add({
            id: SEG_UPGRADE_TRANSCRIPT_DIALOG_ID,
            component: UpgradeTranscriptModalComponent as Type<Component>,
            componentProps: {
                nodeId: id,
                nodeType,
                onClose: () => dialogService.remove(SEG_UPGRADE_TRANSCRIPT_DIALOG_ID),
            },
        });
    }

    /**
     * Return if the Pre-checks transcript modal needs to be opened in readonly state i.e
     * if suppress warning checkbox needs to be shown.
     */
    private openTranscriptModalInReadOnlyMode(): boolean {
        const {
            arePreChecksComplete,
            isCurrentPageUpdate,
            userSelectedOptions,
        } = this;

        return !arePreChecksComplete || !isCurrentPageUpdate || Object.keys(userSelectedOptions).length === 0;
    }

    /**
     * Check if the latest operation is of type pre check only.
     */
    private async checkIfPreCheckOnlyFlow(systemReportRef: string): Promise<boolean> {
        const systemReportCollection = new this.SystemReportCollectionClass();

        this.systemReportId = this.stringService.slug(systemReportRef);

        systemReportCollection.setParams({ 'uuid.in': [this.systemReportId] });

        await systemReportCollection.load();

        const operationType = systemReportCollection.getItemById(this.systemReportId)?.config?.type;

        systemReportCollection.destroy();

        return operationType === PRE_CHECKS;
    }
}
